import { Loader } from "@aix/ui"
import type { FETrack } from "@aix/vidmax-shared/src/remotion/schema/project"
import { ETrackType } from "@aix/vidmax-shared/src/remotion/schema/project"
import { ELEVENLABS_DEFAULT_VOICE_ID } from "@aix/vidmax-shared/src/service/elevenlabs/defaultVoices"
import { createVideoProjectProps } from "@aix/vidmax-shared/src/utils/projectProps"
import { createFileRoute } from "@tanstack/react-router"
import { map } from "lodash-es"
import CustomVideoStudioNav from "~/components/Templates/VideoStudio/CustomVideoStudioNav"
import type { VideoStudioProjectProps } from "~/components/Templates/VideoStudio/VideoStudioContext"
import VideoStudioPlayground from "~/components/Templates/VideoStudio/VideoStudioPlayground"
import { api } from "~/utils/api"

/**
 * Fix text tracks with broken positions and outdated content types.
 * This is for backward compatibility.
 *
 * This won't happen to new videos.
 */
const mapLegacyTextTracks = (tracks: FETrack[]): FETrack[] => {
  return map(tracks, (track) => {
    if (track.type === ETrackType.TEXT) {
      if (typeof track.content === "string") {
        track.content = [
          {
            text: track.content,
            startInSeconds: track.startTime,
            endInSeconds: track.endTime,
            words: [{ word: track.content, startTime: track.startTime, endTime: track.endTime }],
          },
        ]
      }
      track.position.x = track.position.x > 500 ? 0 : track.position.x
    }
    return track
  })
}

const VideoEditorPage = () => {
  const { videoId } = Route.useParams()

  const { data: video, isLoading } = api.video.getVideoById.useQuery({ id: videoId })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[100dvh]">
        <Loader className="text-muted-foreground" />
      </div>
    )
  }

  if (!video) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <span className="font-semibold tracking-widest uppercase">Video not found</span>
      </div>
    )
  }

  // Transform video data into RemotionProjectProps
  const initProjectProps: VideoStudioProjectProps = {
    ...createVideoProjectProps(video),
    voiceId: video?.voiceId || ELEVENLABS_DEFAULT_VOICE_ID,
    script: video?.script || "",
  }

  // Added for backward compatibility
  initProjectProps.tracks = mapLegacyTextTracks(initProjectProps.tracks)

  return (
    <CustomVideoStudioNav video={video}>
      <VideoStudioPlayground video={video} projectProps={initProjectProps} />
    </CustomVideoStudioNav>
  )
}

export interface IEditorSearch {
  from?: string
  beat?: string
  t?: string
}

export const Route = createFileRoute("/videos/$videoId/editor/")({
  /**
   * Every key is OPTIONAL, and `t` stays a string.
   *
   * Optional matters because this route is linked to from the storyboard header and the series
   * list without any search params — a required key there is a compile error at every call site.
   * String matters because `tanstackNavigation` flattens the parsed search object into
   * `URLSearchParams`, which only accepts `Record<string, string>`; a numeric `t` breaks it.
   * The consumer parses to a number.
   */
  validateSearch: (search: Record<string, unknown>): IEditorSearch => ({
    from: typeof search.from === "string" ? search.from : undefined,
    beat: typeof search.beat === "string" ? search.beat : undefined,
    t: typeof search.t === "string" && search.t !== "" && Number.isFinite(Number(search.t)) ? search.t : undefined,
  }),
  component: VideoEditorPage,
})
